import { domainColor } from '../../lib/rubric'

/**
 * Five segments, filled to the current level, in the domain's own hue.
 *
 * Five rather than a continuous bar because the levels are named rungs, not a
 * percentage — a ladder invites "what is the next rung", a percentage invites
 * "how far behind is she". Fractional levels (a domain average) fill the
 * leading segment partly.
 *
 * Confidence is drawn, not written: when we are unsure, the leading segment is
 * hatched and dimmed. That says "this edge is soft" without a word of hedging,
 * and it stops a number that rests on one observation from looking as solid as
 * one that rests on ten.
 */
export function LevelBar({
  level,
  domainKey,
  confidence,
  size = 'md',
  label,
}: {
  level?: number
  domainKey?: string
  /** 0..1. Below 0.6 the leading segment reads as provisional. */
  confidence?: number
  size?: 'sm' | 'md' | 'lg'
  /** Pass only when no adjacent text already says the level, to avoid echoes. */
  label?: string
}) {
  const value = typeof level === 'number' && level > 0 ? Math.min(level, 5) : 0
  const soft = typeof confidence === 'number' && confidence < 0.6
  const leading = Math.ceil(value)

  return (
    <span
      className={`levelbar levelbar-${size}${value === 0 ? ' is-unmeasured' : ''}`}
      style={{ '--domain-color': domainColor(domainKey, 'var(--accent)') } as React.CSSProperties}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {[1, 2, 3, 4, 5].map((segment) => {
        const fill = Math.max(0, Math.min(1, value - (segment - 1)))
        return (
          <span key={segment} className="levelbar-seg">
            {fill > 0 && (
              <span
                className={`levelbar-fill${soft && segment === leading ? ' is-soft' : ''}`}
                style={{ transform: `scaleX(${fill})` }}
              />
            )}
          </span>
        )
      })}
    </span>
  )
}
